import useGetOrderCashTransactionPrice from '../../../../services/hookService/getOrderCashTransactionPrice/useGetOrderCashTransactionPrice';
import useGetStoreOrderCashTransactionPrice from '../../../../services/hookService/getStoreOrderCashTransactionPrice/useGetStoreOrderCashTransactionPrice';
import useGetOrderCreditTransactionPrice from '../../../../services/hookService/useGetOrderCreditTransactionPrice/useGetOrderCreditTransactionPrice';
import useGetStoreOrderCreditTransactionPrice from '../../../../services/hookService/useGetStoreOrderCreditTransactionPrice/useGetStoreOrderCreditTransactionPrice';
import {
  PaymentCondition,
  PaymentMethodValues,
  TransactionTypesChoices,
} from '../utils/helpers';
import {resolvePaymentCondition} from '../utils/resolvePaymentCondition';
import {PaymentResponseTypes} from '../utils/types';

export interface PaymentQueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  isFetching: boolean;
  refetch: () => void;
}

export interface NormalizedPaymentPrice {
  price?: number;
  totalPrice?: number;
  userBalance?: number;
}

export function usePaymentPrice({
  transactionType,
  paymentType,
  orderGuid,
  storeOrderGuid,
  enabled = true,
}: {
  transactionType?: TransactionTypesChoices;
  paymentType?: PaymentMethodValues;
  orderGuid?: string;
  storeOrderGuid?: string;
  enabled?: boolean;
}) {
  const condition = resolvePaymentCondition(transactionType, paymentType);

  const orderCash = useGetOrderCashTransactionPrice({
    orderGuid,
    enabled: enabled && condition === PaymentCondition.ORDER_CASH,
  });

  const storeOrderCash = useGetStoreOrderCashTransactionPrice({
    storeOrderGuid,
    enabled: enabled && condition === PaymentCondition.STORE_ORDER_CASH,
  });

  const orderCredit = useGetOrderCreditTransactionPrice({
    orderGuid,
    paymentType,
    enabled: enabled && condition === PaymentCondition.ORDER_CREDIT,
  });

  const storeOrderCredit = useGetStoreOrderCreditTransactionPrice({
    storeOrderGuid,
    paymentType,
    enabled: enabled && condition === PaymentCondition.STORE_ORDER_CREDIT,
  });

  const emptyQuery: PaymentQueryResult<PaymentResponseTypes> = {
    data: undefined,
    isLoading: false,
    isSuccess: false,
    isFetching: false,
    refetch: () => {},
  };

  // -------- ACTIVE QUERY SELECTION --------
  let activeQuery = emptyQuery;

  switch (condition) {
    case PaymentCondition.ORDER_CASH:
      activeQuery = orderCash;
      break;

    case PaymentCondition.STORE_ORDER_CASH:
      activeQuery = storeOrderCash;
      break;

    case PaymentCondition.ORDER_CREDIT:
      activeQuery = orderCredit;
      break;

    case PaymentCondition.STORE_ORDER_CREDIT:
      activeQuery = storeOrderCredit;
      break;

    default:
      activeQuery = emptyQuery;
  }

  // -------- PRICE NORMALIZATION --------
  const allAvailablePrices: NormalizedPaymentPrice = (() => {
    const data = activeQuery.data;
    if (!data) return {};

    return {
      price: Number(data.price_to_pay),
      totalPrice: 'total_price' in data ? Number(data.total_price) : undefined,
      userBalance:
        'user_balance' in data ? Number(data.user_balance) : undefined,
    };
  })();

  return {
    ...allAvailablePrices,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    isSuccess: activeQuery.isSuccess,
    refetch: activeQuery.refetch,
    condition,
    hasValidConfig: condition !== PaymentCondition.INVALID,
  };
}
